/**
 * Pipedrive Dashboard Page
 * Shows entity counts and summary statistics
 */

import { usePipedriveStats } from '@/hooks/use-pipedrive-api'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Link } from 'react-router-dom'
import { CATEGORICAL } from '@/lib/theme'
import {
  Building2,
  Users,
  Briefcase,
  Target,
  Calendar,
  StickyNote,
  Package,
  FileText,
  CheckCircle,
  Clock,
  XCircle,
} from 'lucide-react'

interface StatCardProps {
  title: string
  value: number | string
  icon: React.ReactNode
  href: string
  iconColor?: string
}

function StatCard({ title, value, icon, href, iconColor }: StatCardProps) {
  return (
    <Link to={href}>
      <Card className="hover:shadow-md transition-shadow cursor-pointer">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
          <div style={{ color: iconColor }}>{icon}</div>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{value.toLocaleString()}</div>
        </CardContent>
      </Card>
    </Link>
  )
}

function StatCardSkeleton() {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-5 w-5" />
      </CardHeader>
      <CardContent>
        <Skeleton className="h-8 w-16" />
      </CardContent>
    </Card>
  )
}

export default function PipedriveDashboard() {
  const { data, isLoading, error } = usePipedriveStats()

  if (error) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-6">Pipedrive Dashboard</h1>
        <div className="bg-destructive/10 text-destructive p-4 rounded-lg">
          Failed to load statistics. Please try again later.
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Pipedrive Dashboard</h1>
        <p className="text-muted-foreground">Overview of your imported Pipedrive data</p>
      </div>

      {/* Entity Counts */}
      <section aria-labelledby="entity-counts-heading">
        <h2 id="entity-counts-heading" className="sr-only">
          Entity Counts
        </h2>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {isLoading ? (
            <>
              <StatCardSkeleton />
              <StatCardSkeleton />
              <StatCardSkeleton />
              <StatCardSkeleton />
              <StatCardSkeleton />
              <StatCardSkeleton />
              <StatCardSkeleton />
              <StatCardSkeleton />
            </>
          ) : (
            <>
              <StatCard
                title="Organizations"
                value={data?.counts.organizations ?? 0}
                icon={<Building2 className="h-5 w-5" />}
                href="/pipedrive/organizations"
                iconColor={CATEGORICAL.navy.solid}
              />
              <StatCard
                title="People"
                value={data?.counts.people ?? 0}
                icon={<Users className="h-5 w-5" />}
                href="/pipedrive/people"
                iconColor={CATEGORICAL.evergreen.solid}
              />
              <StatCard
                title="Deals"
                value={data?.counts.deals ?? 0}
                icon={<Briefcase className="h-5 w-5" />}
                href="/pipedrive/deals"
                iconColor={CATEGORICAL.plum.solid}
              />
              <StatCard
                title="Leads"
                value={data?.counts.leads ?? 0}
                icon={<Target className="h-5 w-5" />}
                href="/pipedrive/leads"
                iconColor={CATEGORICAL.clay.solid}
              />
              <StatCard
                title="Activities"
                value={data?.counts.activities ?? 0}
                icon={<Calendar className="h-5 w-5" />}
                href="/pipedrive/activities"
                iconColor={CATEGORICAL.teal.solid}
              />
              <StatCard
                title="Notes"
                value={data?.counts.notes ?? 0}
                icon={<StickyNote className="h-5 w-5" />}
                href="/pipedrive/notes"
                iconColor={CATEGORICAL.slate.solid}
              />
              <StatCard
                title="Products"
                value={data?.counts.products ?? 0}
                icon={<Package className="h-5 w-5" />}
                href="/pipedrive/products"
                iconColor={CATEGORICAL.raspberry.solid}
              />
              <StatCard
                title="Files"
                value={data?.counts.files ?? 0}
                icon={<FileText className="h-5 w-5" />}
                href="/pipedrive/files"
                iconColor={CATEGORICAL.steel.solid}
              />
            </>
          )}
        </div>
      </section>

      {/* Deal Status Breakdown */}
      {!isLoading && data?.dealsByStatus && data.dealsByStatus.length > 0 && (
        <section aria-labelledby="deals-status-heading">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="h-5 w-5" aria-hidden="true" />
                Deals by Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-3">
                {data.dealsByStatus.map((status) => (
                  <div
                    key={status.status}
                    className="flex items-center justify-between p-4 bg-muted rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      {status.status === 'won' && (
                        <CheckCircle
                          className="h-5 w-5"
                          style={{ color: CATEGORICAL.evergreen.solid }}
                        />
                      )}
                      {status.status === 'open' && (
                        <Clock className="h-5 w-5" style={{ color: CATEGORICAL.navy.solid }} />
                      )}
                      {status.status === 'lost' && (
                        <XCircle
                          className="h-5 w-5"
                          style={{ color: CATEGORICAL.raspberry.solid }}
                        />
                      )}
                      <div>
                        <p className="font-medium capitalize">{status.status}</p>
                        <p className="text-sm text-muted-foreground">
                          {status.count.toLocaleString()} deals
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-lg">
                        ${(status.total_value ?? 0).toLocaleString()}
                      </p>
                      <p className="text-xs text-muted-foreground">Total value</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </section>
      )}

      {/* Activity Status */}
      {!isLoading && data?.activitiesByDone && data.activitiesByDone.length > 0 && (
        <section aria-labelledby="activities-status-heading">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" aria-hidden="true" />
                Activities Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2">
                {data.activitiesByDone.map((status) => (
                  <div
                    key={status.done}
                    className="flex items-center justify-between p-4 bg-muted rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      {status.done === 'Yes' ? (
                        <CheckCircle
                          className="h-5 w-5"
                          style={{ color: CATEGORICAL.evergreen.solid }}
                        />
                      ) : (
                        <Clock className="h-5 w-5" style={{ color: CATEGORICAL.clay.solid }} />
                      )}
                      <p className="font-medium">
                        {status.done === 'Yes' ? 'Completed' : 'Pending'}
                      </p>
                    </div>
                    <p className="font-bold text-lg">{status.count.toLocaleString()}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </section>
      )}
    </div>
  )
}
